import React, { useState } from "react";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { CalendarIcon, PlusCircle, Trash2, Info } from "lucide-react";
import { useNavigate } from "react-router-dom";

import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
// Switch is not used for bankHolidaysIncluded anymore as it's hardcoded
// import { Switch } from "@/components/ui/switch";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useToast } from "@/components/ui/use-toast";
// Label is not explicitly used, can be removed if not needed by other parts
// import { Label } from "@/components/ui/label";
import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";
import type { GenerateAgreementResponse } from "@/types/employment-agreement"; // Assuming this response type is reusable
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { cn } from "@/lib/utils";

// Import shared layout components
import PublicNavBar from "@/components/layout/PublicNavBar";
import PublicFooter from "@/components/layout/PublicFooter";

// Import family image
import wholeFamily from "@/images/wholeFamily.webp";

// Define the type for the form data based on the schema
// This should ideally live in your types file, e.g., src/types/employment-agreement.ts
const liteFormSchema = z.object({
  employerName: z.string().min(2, "Name must be at least 2 characters"),
  employerAddress: z.string().min(5, "Please enter a valid address"),
  employeeName: z.string().min(2, "Name must be at least 2 characters"),
  employeeAddress: z.string().min(5, "Please enter a valid address"),
  startDate: z.string().refine((val) => val && val.length > 0, {
    message: "Start date is required",
  }), // Ensure it's not empty
  probationPeriod: z.number().min(1).max(12),

  // childrenDetails removed

  workAddress: z.string().min(5, "Please enter a valid work address"),
  weeklyHours: z.number().min(1).max(48),
  workStartTime: z.string().refine((val) => val && val.length > 0, {
    message: "Work start time is required",
  }),
  workEndTime: z.string().refine((val) => val && val.length > 0, {
    message: "Work end time is required",
  }),
  workDays: z
    .array(z.string())
    .min(1, "At least one work day must be selected"),
  overtimeRate: z.number().min(1),

  salaryAmount: z.number().min(1),
  // paymentDate is hardcoded
  paymentMethod: z.enum(["bank_transfer", "other"]),

  // annualLeaveWeeks and bankHolidaysIncluded are hardcoded
  noticePeriod: z.string().refine((val) => val && val.length > 0, {
    message: "Notice period is required",
  }),
  additionalDuties: z.array(
    z.object({
      id: z.string(),
      value: z.string().min(1, "Duty description is required"),
    }),
  ),

  // New fields for user contact
  userFirstName: z.string().min(1, "Your first name is required"),
  userLastName: z.string().min(1, "Your last name is required"),
  userEmail: z.string().email("Please enter a valid email address"),
});

export type LiteEmploymentAgreementFormData = z.infer<typeof liteFormSchema>;

// Hardcoded values
const HARDCODED_PAYMENT_DATE = "last working day";
const HARDCODED_ANNUAL_LEAVE_WEEKS = 5.6;
const HARDCODED_BANK_HOLIDAYS_INCLUDED = true;

export default function LiteEmploymentAgreementForm() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<LiteEmploymentAgreementFormData>({
    resolver: zodResolver(liteFormSchema),
    defaultValues: {
      employerName: "",
      employerAddress: "",
      employeeName: "",
      employeeAddress: "",
      startDate: "",
      probationPeriod: 3,
      workAddress: "",
      weeklyHours: 40,
      workStartTime: "09:00",
      workEndTime: "17:00",
      overtimeRate: 1.5,
      workDays: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      salaryAmount: undefined, // Or a suitable default like 0, but validation will catch it
      paymentMethod: "bank_transfer",
      noticePeriod: "",
      additionalDuties: [],
      userFirstName: "",
      userLastName: "",
      userEmail: "",
    },
  });

  const {
    fields: dutiesFields,
    append: appendDuty,
    remove: removeDuty,
  } = useFieldArray({
    control: form.control,
    name: "additionalDuties",
  });

  async function onSubmit(data: LiteEmploymentAgreementFormData) {
    try {
      setIsSubmitting(true);

      const agreementPayload = {
        ...data,
        paymentDate: HARDCODED_PAYMENT_DATE,
        annualLeaveWeeks: HARDCODED_ANNUAL_LEAVE_WEEKS,
        bankHolidaysIncluded: HARDCODED_BANK_HOLIDAYS_INCLUDED,
      };

      // Ensure EndpointKeys.GENERATE_LITE_AGREEMENT is defined in src/config/api.ts
      const response = await ApiService.request<GenerateAgreementResponse>(
        EndpointKeys.GENERATE_LITE_AGREEMENT, // This will be a new endpoint key
        {
          method: "POST",
          body: { agreementData: agreementPayload },
          skipAuth: true,
        },
      );

      if (response.status === "success" && response.agreement_id) {
        toast({
          title: "Success",
          description:
            "Your employment agreement has been generated. Please check your email.", // Or redirect to a success page
        });
        // navigate("/some-success-page"); // Or clear form, etc.
        form.reset(); // Clear the form on success
      } else {
        throw new Error(response.error || "Failed to generate agreement");
      }
    } catch (error) {
      toast({
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : "Failed to generate agreement",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="flex flex-col items-center min-h-screen bg-background">
      <PublicNavBar />

      <div className="w-full max-w-4xl space-y-6 px-4 py-8 md:py-12">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6 bg-white rounded-lg p-6 shadow-lg">
          <div className="text-center md:text-left">
            <h1 className="text-3xl text-foreground">
              Generate Employment Contract
            </h1>
            <p className="text-muted-foreground mt-2">
              Fill in the details below to generate a simplified employment
              agreement.
            </p>
            <Button
              className="mt-4"
              onClick={() =>
                window.open("https://www.hirelo.io/demo", "_blank")
              }
            >
              Try Demo
            </Button>
          </div>
          <div className="w-full md:w-1/2 relative">
            <img
              src={wholeFamily}
              alt="Family with child"
              className="rounded-lg object-cover shadow-md w-full max-h-64 md:max-h-none"
            />
          </div>
        </div>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
            <Alert className="mb-4 bg-white border-primary/20">
              <Info className="h-4 w-4 text-primary" />
              <AlertTitle>Employment Contract Generator</AlertTitle>
              <AlertDescription>
                This tool helps you quickly generate a basic employment
                agreement. Please fill in all required fields accurately. The
                generated document is a simplified version and may not cover all
                specific circumstances. You'll receive the document at the email
                address you provide.
              </AlertDescription>
            </Alert>

            {/* Employer & Employee Information */}
            <Card>
              <CardHeader className="bg-muted/50">
                <CardTitle>Basic Information</CardTitle>
                <CardDescription>
                  Enter the employer and employee details.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6 pt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="employerName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Employer Name</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder="e.g., Jane Doe Services"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="employerAddress"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Employer Address</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder="e.g., 123 Employer St, London"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="employeeName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Employee (Nanny) Name</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="e.g., John Smith" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="employeeAddress"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Employee (Nanny) Address</FormLabel>
                        <FormControl>
                          <Input
                            {...field}
                            placeholder="e.g., 456 Nanny Ave, London"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="startDate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Employment Start Date</FormLabel>
                        <Popover>
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                variant="outline"
                                className={cn(
                                  "w-full justify-start text-left font-normal",
                                  !field.value && "text-muted-foreground",
                                )}
                              >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {field.value ? (
                                  format(
                                    new Date(field.value + "T00:00:00"),
                                    "dd/MM/yyyy",
                                  ) // Ensure date is parsed correctly
                                ) : (
                                  <span>Pick a date</span>
                                )}
                              </Button>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0 z-[100]">
                            <Calendar
                              mode="single"
                              selected={
                                field.value
                                  ? new Date(field.value + "T00:00:00")
                                  : undefined
                              }
                              onSelect={(date) =>
                                field.onChange(
                                  date ? format(date, "yyyy-MM-dd") : "",
                                )
                              }
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="probationPeriod"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Probation Period (months)</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            {...field}
                            onChange={(e) =>
                              field.onChange(parseInt(e.target.value, 10) || 0)
                            }
                            placeholder="e.g., 3"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Work Schedule */}
            <Card>
              <CardHeader className="bg-muted/50">
                <CardTitle>Work Schedule</CardTitle>
                <CardDescription>
                  Define the working hours and location.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6 pt-6">
                <FormField
                  control={form.control}
                  name="workAddress"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Primary Work Address</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="e.g., Same as Employer Address or specify"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <FormField
                    control={form.control}
                    name="weeklyHours"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Total Weekly Hours</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            {...field}
                            onChange={(e) =>
                              field.onChange(parseInt(e.target.value, 10) || 0)
                            }
                            placeholder="e.g., 40"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="workStartTime"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Daily Start Time</FormLabel>
                        <FormControl>
                          <Input type="time" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="workEndTime"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Daily End Time</FormLabel>
                        <FormControl>
                          <Input type="time" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={form.control}
                  name="workDays"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Work Days</FormLabel>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2">
                        {[
                          "Monday",
                          "Tuesday",
                          "Wednesday",
                          "Thursday",
                          "Friday",
                          "Saturday",
                          "Sunday",
                        ].map((day) => (
                          <FormField
                            key={day}
                            control={form.control}
                            name="workDays"
                            render={(
                              { field: dayField }, // Renamed to avoid conflict
                            ) => (
                              <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                                <FormControl>
                                  <Input
                                    type="checkbox"
                                    className="h-4 w-4"
                                    checked={dayField.value?.includes(day)}
                                    onChange={(e) => {
                                      const currentValues =
                                        dayField.value || [];
                                      if (e.target.checked) {
                                        dayField.onChange([
                                          ...currentValues,
                                          day,
                                        ]);
                                      } else {
                                        dayField.onChange(
                                          currentValues.filter(
                                            (value) => value !== day,
                                          ),
                                        );
                                      }
                                    }}
                                  />
                                </FormControl>
                                <FormLabel className="font-normal">
                                  {day}
                                </FormLabel>
                              </FormItem>
                            )}
                          />
                        ))}
                      </div>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>

            {/* Compensation */}
            <Card>
              <CardHeader className="bg-muted/50">
                <CardTitle>Compensation</CardTitle>
                <CardDescription>
                  Set salary and payment details. Overtime rate, payment date,
                  annual leave, and bank holiday inclusion are fixed for this
                  simplified version.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6 pt-6">
                <FormField
                  control={form.control}
                  name="salaryAmount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Annual Gross Salary Amount (£)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          {...field}
                          onChange={(e) =>
                            field.onChange(parseFloat(e.target.value) || 0)
                          }
                          placeholder="e.g., 30000"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="paymentMethod"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Payment Method</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select payment method" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="bank_transfer">
                            Bank Transfer
                          </SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="overtimeRate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Overtime Rate (multiplier)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.1"
                          {...field}
                          onChange={(e) =>
                            field.onChange(parseFloat(e.target.value) || 0)
                          }
                          placeholder="e.g., 1.5"
                        />
                      </FormControl>
                      <FormDescription>
                        Standard overtime rate is 1.5 times the regular pay.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {/* Payment Date is hardcoded: "Last Working Day" */}
                <FormItem>
                  <FormLabel>Payment Date</FormLabel>
                  <Input
                    value="Last working day of the month"
                    disabled
                    className="bg-muted/50"
                  />
                  <FormDescription>
                    This is fixed for the simplified agreement.
                  </FormDescription>
                </FormItem>
              </CardContent>
            </Card>

            {/* Leave and Notice Period */}
            <Card>
              <CardHeader className="bg-muted/50">
                <CardTitle>Leave and Notice Period</CardTitle>
                <CardDescription>
                  Annual leave and bank holiday inclusion are fixed. Configure
                  notice period.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6 pt-6">
                <FormItem>
                  <FormLabel>Annual Leave</FormLabel>
                  <Input
                    value={`${HARDCODED_ANNUAL_LEAVE_WEEKS} weeks (including bank holidays)`}
                    disabled
                    className="bg-muted/50"
                  />
                  <FormDescription>
                    Statutory minimum, bank holidays are part of this allowance.
                  </FormDescription>
                </FormItem>
                <FormField
                  control={form.control}
                  name="noticePeriod"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Notice Period</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select notice period" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="1 week">
                            1 Week (statutory minimum during first month)
                          </SelectItem>
                          <SelectItem value="statutory">
                            Statutory (1 week for first 2 years, then 1 week per
                            year up to 12 weeks)
                          </SelectItem>
                          <SelectItem value="1 month">1 Month</SelectItem>
                          <SelectItem value="2 months">2 Months</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </CardContent>
            </Card>

            {/* Additional Duties */}
            <Card>
              <CardHeader className="bg-muted/50">
                <CardTitle>Additional Duties (Optional)</CardTitle>
                <CardDescription>
                  Specify any additional responsibilities.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 pt-6">
                <div className="space-y-4">
                  {dutiesFields.map((item, index) => (
                    <div key={item.id} className="flex items-center gap-4">
                      <FormField
                        control={form.control}
                        name={`additionalDuties.${index}.value`}
                        render={({ field }) => (
                          <FormItem className="flex-1">
                            {index === 0 && (
                              <FormLabel className="sr-only">
                                Duty Description
                              </FormLabel>
                            )}
                            <FormControl>
                              <Input
                                {...field}
                                placeholder="Enter additional duty"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => removeDuty(index)}
                        aria-label="Remove duty"
                        className="text-destructive hover:text-destructive/80 hover:bg-destructive/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() =>
                      appendDuty({ id: Date.now().toString(), value: "" })
                    }
                  >
                    <PlusCircle className="h-4 w-4 mr-2" />
                    Add Duty
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* User Contact Information */}
            <Card>
              <CardHeader className="bg-muted/50">
                <CardTitle>Your Contact Information</CardTitle>
                <CardDescription>
                  Please provide your details to receive the generated
                  agreement.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6 pt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <FormField
                    control={form.control}
                    name="userFirstName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Your First Name</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="e.g., Alex" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="userLastName"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Your Last Name</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="e.g., Taylor" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <FormField
                  control={form.control}
                  name="userEmail"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Your Email Address</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          {...field}
                          placeholder="e.g., alex.taylor@example.com"
                        />
                      </FormControl>
                      <FormDescription>
                        The generated agreement will be sent here.
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="border rounded-md p-4 bg-muted/50">
                  <div className="flex flex-col md:flex-row items-center gap-4">
                    <div className="text-sm text-muted-foreground">
                      <p className="font-medium text-foreground">
                        Need help with your employment agreement?
                      </p>
                      <p>
                        Hirelo offers expert guidance on employment
                        relationships and payroll compliance.
                      </p>
                    </div>
                    <Button
                      className="whitespace-nowrap"
                      onClick={() =>
                        window.open("https://www.hirelo.io/contact", "_blank")
                      }
                    >
                      Contact Support
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Form Actions */}
            <div className="flex flex-col sm:flex-row justify-end gap-4 pt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => form.reset()} // Or navigate back if there's a previous page
                disabled={isSubmitting}
                className="w-full sm:w-auto"
              >
                Clear Form
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full sm:w-auto"
              >
                {isSubmitting ? "Generating..." : "Generate Agreement"}
              </Button>
            </div>
          </form>
        </Form>

        <div className="mt-8">
          <PublicFooter />
        </div>
      </div>
    </div>
  );
}
